import type { Address } from "@nmt/shared";
import { useEffect, useRef, useState } from "react";
import { get } from "../api";
import { Field } from "./ui";

type Suggestion = { id: string; label: string };
export type Placed = Address & { lat: number | null; lon: number | null };

/**
 * The street field, with addresses suggested as you type (Amazon Location). Picking one fills the
 * street, city, state, and ZIP; typing your own still works.
 */
export function StreetWithSuggestions({ value, onType, onPick, className, near }: { value: string; onType: (v: string) => void; onPick: (p: Placed) => void; className?: string; near?: { lat: number; lon: number } | null }) {
  const [list, setList] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!typed.current || value.trim().length < 4) {
      setList([]);
      return;
    }
    const t = window.setTimeout(() => {
      const qs = new URLSearchParams({ q: value.trim() });
      if (near) (qs.set("lat", String(near.lat)), qs.set("lon", String(near.lon)));
      get<Suggestion[]>(`/api/places/suggest?${qs}`)
        .then((s) => (setList(s), setOpen(s.length > 0), setActive(-1)))
        .catch(() => setList([]));
    }, 300);
    return () => window.clearTimeout(t);
  }, [value]);
  useEffect(() => {
    const close = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const pick = async (s: Suggestion) => {
    setOpen(false);
    typed.current = false;
    try {
      const p = await get<{ street: string; city: string; state: string; zip: string; lat: number | null; lon: number | null }>(`/api/places/place/${encodeURIComponent(s.id)}`);
      onPick({ street: p.street, street2: "", city: p.city, state: p.state || "OK", zip: p.zip, lat: p.lat, lon: p.lon });
    } catch {
      // Couldn't get the details: keep what's typed.
    }
  };
  return (
    <div ref={box} className={`relative ${className ?? ""}`}>
      <Field
        label="Street"
        value={value}
        autoComplete="off"
        onChange={(e) => {
          typed.current = true;
          onType(e.target.value);
        }}
        onFocus={() => list.length && setOpen(true)}
        onKeyDown={(e) => {
          if (!open || !list.length) return;
          if (e.key === "ArrowDown") (e.preventDefault(), setActive((a) => Math.min(list.length - 1, a + 1)));
          else if (e.key === "ArrowUp") (e.preventDefault(), setActive((a) => Math.max(0, a - 1)));
          else if (e.key === "Enter" && active >= 0) (e.preventDefault(), void pick(list[active]!));
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && (
        <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-[var(--radius-control)] border border-line bg-white shadow-lg" role="listbox">
          {list.map((s, i) => (
            <li key={s.id}>
              <button type="button" className={`block w-full px-3 py-2 text-left text-sm hover:bg-mist ${i === active ? "bg-mist" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => void pick(s)}>
                {s.label}
              </button>
            </li>
          ))}
          <li className="px-3 py-1 text-right text-[11px] text-muted">Addresses from Amazon Location</li>
        </ul>
      )}
    </div>
  );
}
